/**
 * PATH: packages/ui/src/kanban/components/card-detail/EmailLinkEmptyState.tsx
 * FILE_ID: UI-KANBAN-CMP-EMAIL-EMPTY-001
 * PURPOSE: The Email tab's empty state for cards with no linked email and
 *          no email-shaped title (i.e. hand-typed task cards). Gives the
 *          operator a way to LINK an email so a thread surfaces here.
 * WHY: Operator (Newmark, 2026-06-22) expects every card to carry an
 *      Email/threading option, like Bravada's email-derived cards. Before
 *      this, manual cards had no Email tab at all. The always-on tab needs
 *      a sensible empty state rather than the synthesised <EmailFromCard>
 *      view (which is meaningless for a non-email title).
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────────┐   ┌────────────────────┐
 *   │ no link, not │ → │ Browse emails (rail) │ → │ drag/allocate, or  │
 *   │ email-shaped │   │ or Search Email Hub  │   │ auto-match by job  │
 *   └──────────────┘   └─────────────────────┘   └────────────────────┘
 * DEPENDENCIES:
 *   - lucide-react Mail / Search / Inbox icons (presentational only).
 * RELATED:
 *   - CardDetailDrawer.tsx: renders this in the email pane third branch.
 *   - email_kanban match_emails_to_cards (BE): auto-links by job so this
 *     state resolves itself once an email matches the card's job.
 */

import type { ReactNode } from 'react'
import { Inbox, Mail, PenSquare, Search } from 'lucide-react'

export interface EmailLinkEmptyStateProps {
  /** Opens the drag-to-allocate emails rail (host wires the rail). When
   *  omitted (e.g. non-inline drawer) the button is hidden. */
  onBrowseEmails?: () => void
  /** Deep-link into the Email Hub search prefilled with the card's text.
   *  ARC-1 MR4: FALLBACK ONLY — hidden whenever `searchSlot` is provided
   *  (in-drawer search replaces the redirect). When omitted (no email
   *  adapter) the link is hidden. */
  searchUrl?: string | null
  /** ARC-1 MR2 — open the inline composer to send a NEW email from this
   *  card (sent copy links back via `linked_card_ids`). Hidden when the
   *  adapter ships no inline composer. */
  onCompose?: () => void
  /** ARC-1 MR4 — in-place email search (see EmailSearchInline). When
   *  present it renders under the buttons and the `searchUrl` redirect
   *  is suppressed: the operator searches + links without leaving the
   *  card. */
  searchSlot?: ReactNode
}

export function EmailLinkEmptyState({
  onBrowseEmails,
  searchUrl,
  onCompose,
  searchSlot,
}: EmailLinkEmptyStateProps) {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-12 text-center text-neutral-500 dark:text-zinc-400"
      data-testid="email-link-empty-state"
    >
      <Mail className="h-8 w-8 opacity-40" aria-hidden />
      <p className="text-sm font-medium text-neutral-700 dark:text-zinc-200">
        No email linked yet
      </p>
      <p className="max-w-[300px] text-[11.5px] leading-snug">
        Link an email to this card and its conversation will show up here.
        Browse the emails rail and drop one on, or it links itself once an
        email is matched to this card's job.
      </p>
      {(onBrowseEmails || searchUrl || onCompose) && (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
          {onCompose && (
            <button
              type="button"
              onClick={onCompose}
              data-testid="email-empty-compose"
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              <PenSquare className="h-3.5 w-3.5" aria-hidden />
              Write email
            </button>
          )}
          {onBrowseEmails && (
            <button
              type="button"
              onClick={onBrowseEmails}
              className="inline-flex items-center gap-1.5 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-[12px] font-medium text-neutral-700 shadow-sm transition-colors hover:bg-neutral-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              <Inbox className="h-3.5 w-3.5" aria-hidden />
              Browse emails
            </button>
          )}
          {!searchSlot && searchUrl && (
            <a
              href={searchUrl}
              data-testid="email-empty-search-redirect"
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium text-blue-600 transition-colors hover:bg-blue-50 hover:underline dark:text-blue-400 dark:hover:bg-blue-950/40"
            >
              <Search className="h-3.5 w-3.5" aria-hidden />
              Search the Email Hub
            </a>
          )}
        </div>
      )}
      {searchSlot && <div className="mt-2 w-full max-w-[360px]">{searchSlot}</div>}
    </div>
  )
}
